import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './theme.css';
import {
  BrowserRouter,
  Route,
  Routes,
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  Navbar,
  Nav,
  Container,
  Button,
  NavDropdown,
  Alert,
} from 'react-bootstrap';
import Dashboard from './pages/Dashboard';
import Inventory from './pages/Inventory';
import Drafts from './components/Drafts';
import DraftForm from './components/DraftForm';
import Editor from './pages/Editor';
import AddProduct from './pages/AddProduct';
import ShopifyInventory from './pages/ShopifyInventory';
import EbayInventory from './pages/EbayInventory';
import Login from './pages/Login';
import Orders from './pages/Orders';
import Platforms from './pages/Platforms';
import Products from './pages/Products';
import Reports from './pages/Reports';
import WalmartInventory from './pages/WalmartInventory';
import ProtectedRoute from './components/ProtectedRoute';
import PrivacyPolicy from './pages/PrivacyPolicy';
import SKUGenerator from './pages/Tools/SKUGenerator';
import BarcodeGenerator from './pages/Tools/BarcodeGenerator';
import BinAssignment from './pages/BinAssignment';
import BulkUploader from './pages/Tools/BulkUploader';
import PickList from './pages/PickList';
import api from './api';
import Settings from './pages/Settings';
import Admin from './pages/Admin';
import AddUser from './pages/AddUser';
import Listings from './pages/Listings';
import ConnectedAccounts from './pages/ConnectedAccounts';
import Accounting from './pages/Accounting';
import AccountingMercari from './pages/AccountingMercari';
import AccountingPoshmark from './pages/AccountingPoshmark';
import AccountingShopify from './pages/AccountingShopify';
import AccountingWalmart from './pages/AccountingWalmart';
import CreateOrder from './pages/Tools/CreateOrder';
import AccountingAll from './pages/AccountingAll';
import Purchases from './pages/Purchases';
import PurchaseBatches from './pages/PurchaseBatches';
import CalendarSettings from './pages/CalendarSettings';
import Restock from './pages/Restock';
import QuickBuy from './pages/QuickBuy';
import Goals from './pages/Goals';
import Budget from './pages/Budget';
import QuickNotesDropdown from './components/QuickNotesDropdown';
import TodoTasksDropdown from './components/TodoTasksDropdown';
import CardIntakeMonitor from './pages/CardIntakeMonitor';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [role, setRole] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState(
    localStorage.getItem('theme') || 'light'
  );

  const isVA = String(role || '').trim().toUpperCase() === 'VA';

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const storedRole = localStorage.getItem('role');
      const isAuth = !!token;

      const normalizedRole = storedRole
        ? storedRole.trim().toUpperCase()
        : null;

      setIsAuthenticated(isAuth);
      setRole(normalizedRole);

      console.log(
        `[${new Date().toISOString()}] [App] Auth check: ` +
          `isAuthenticated=${isAuth}, role=${normalizedRole}`
      );
    };

    checkAuth();

    window.addEventListener('storage', checkAuth);

    return () => {
      window.removeEventListener('storage', checkAuth);
    };
  }, [location.pathname]);

  useEffect(() => {
  const isAllowedVARoute =
    location.pathname === '/quick-buy' ||
    location.pathname === '/tools/sku' ||
    location.pathname === '/inventory/drafts' ||
    location.pathname === '/inventory/drafts/new' ||
    location.pathname.startsWith('/inventory/drafts/edit/') ||
    location.pathname === '/login' ||
    location.pathname === '/privacy';

  if (
    isAuthenticated &&
    isVA &&
    !isAllowedVARoute
  ) {
    navigate('/quick-buy', { replace: true });
  }
}, [
  isAuthenticated,
  isVA,
  location.pathname,
  navigate,
]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const handleLogout = async () => {
    try {
      console.log(
        `[${new Date().toISOString()}] [App] Initiating logout...`
      );

      await api.get('/logout');

      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('role');
      localStorage.removeItem('ebayProducts');
      localStorage.removeItem('ebayCategoryFeeRates');

      setRole(null);
      setIsAuthenticated(false);

      navigate('/login');

      console.log(
        `[${new Date().toISOString()}] [App] Logged out successfully`
      );

      alert('Logged out successfully!');
    } catch (err) {
      const errorMsg =
        err.response?.data?.error ||
        err.message;

      console.error(
        `[${new Date().toISOString()}] [App] Logout error:`,
        errorMsg
      );

      setError(`Failed to logout: ${errorMsg}`);
    }
  };

  return (
    <div>
      {error && (
        <Alert
          variant="danger"
          onClose={() => setError(null)}
          dismissible
        >
          {error}
        </Alert>
      )}

      <Navbar bg="primary" variant="dark" expand="lg">
        <Container>
          <Navbar.Brand
            as={Link}
            to={isVA ? '/quick-buy' : '/'}
          >
            {isVA ? 'Quick Buy' : 'Dashboard'}
          </Navbar.Brand>

          <Navbar.Toggle aria-controls="basic-navbar-nav" />

          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ms-auto">
              {isAuthenticated ? (
                isVA ? (
                  <>
                    <Nav.Link
                      as={Link}
                      to="/quick-buy"
                    >
                      Quick Buy
                    </Nav.Link>

                    <Nav.Link
  as={Link}
  to="/tools/sku"
>
  SKU Generator
</Nav.Link>

                    <Button
                      variant="light"
                      className="ms-2"
                      onClick={handleLogout}
                    >
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Nav.Link
                      as={Link}
                      to="/inventory"
                    >
                      Inventory
                    </Nav.Link>

                    <Nav.Link
                      as={Link}
                      to="/listings"
                    >
                      Listings
                    </Nav.Link>

                    <NavDropdown
                      title="Platforms"
                      id="platforms-dropdown"
                    >
                      <NavDropdown.Item
                        as={Link}
                        to="/platforms/shopify"
                      >
                        Shopify
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/platforms/ebay"
                      >
                        eBay
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/platforms/walmart"
                      >
                        Walmart
                      </NavDropdown.Item>
                    </NavDropdown>

                    <Nav.Link
                      as={Link}
                      to="/orders"
                    >
                      Orders
                    </Nav.Link>

                    <NavDropdown
                      title="Purchases"
                      id="purchases-dropdown"
                    >
                      <NavDropdown.Item
  as={Link}
  to="/purchases"
>
  Purchases
</NavDropdown.Item>

<NavDropdown.Item
  as={Link}
  to="/purchase-batches"
>
  Purchase Batches
</NavDropdown.Item>

<NavDropdown.Item
  as={Link}
  to="/quick-buy"
>
  Quick Buy
</NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/goals"
                      >
                        Goals
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/budget"
                      >
                        Budget
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/restock"
                      >
                        Restock
                      </NavDropdown.Item>
                    </NavDropdown>

                    <Nav.Link
                      as={Link}
                      to="/reports"
                    >
                      Reports
                    </Nav.Link>

                    <Nav.Link
                      as={Link}
                      to="/accounting"
                    >
                      Accounting
                    </Nav.Link>

                    {role === 'OWNER' && (
                      <Nav.Link
                        as={Link}
                        to="/settings"
                      >
                        Settings
                      </Nav.Link>
                    )}

                    <NavDropdown
                      title="Tools"
                      id="tools-dropdown"
                    >
                      <NavDropdown.Item
                        as={Link}
                        to="/tools/sku"
                      >
                        SKU Generator
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/tools/create-order"
                      >
                        Create Order
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/tools/barcode"
                      >
                        Barcode Generator
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/tools/bulk-uploader"
                      >
                        Bulk Uploader
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/bin-assignment"
                      >
                        Bin Assignment
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/picklist"
                      >
                        Pick List
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        as={Link}
                        to="/card-intake-monitor"
                      >
                        Card Intake Monitor
                      </NavDropdown.Item>
                    </NavDropdown>

                    <Button
                      variant="light"
                      className="ms-2"
                      onClick={handleLogout}
                    >
                      Logout
                    </Button>
                  </>
                )
              ) : (
                <Nav.Link
                  as={Link}
                  to="/login"
                >
                  Login
                </Nav.Link>
              )}

              <Nav.Link
                as={Link}
                to="/privacy"
              >
                Privacy Policy
              </Nav.Link>

              {isAuthenticated && !isVA && (
  <NavDropdown
    title="Quick Access"
    id="quick-access-dropdown"
    align="end"
  >
    <div
      style={{
        padding: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        minWidth: '160px',
      }}
    >
      <TodoTasksDropdown />
      <QuickNotesDropdown />
    </div>
  </NavDropdown>
)}

              <Button
                variant="outline-light"
                className="ms-2"
                onClick={() =>
                  setTheme((currentTheme) =>
                    currentTheme === 'dark'
                      ? 'light'
                      : 'dark'
                  )
                }
                title="Toggle dark mode"
              >
                {theme === 'dark' ? '☀️' : '🌙'}
              </Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Routes>
        <Route
          path="/login"
          element={
            <Login
              setIsAuthenticated={setIsAuthenticated}
            />
          }
        />

        <Route
          path="/privacy"
          element={<PrivacyPolicy />}
        />

        <Route
          path="/"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <Dashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/inventory"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <Inventory />
            </ProtectedRoute>
          }
        />

        <Route
          path="/inventory/products"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <Inventory />
            </ProtectedRoute>
          }
        />

        <Route
          path="/listings"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <Listings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/inventory/drafts"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <Drafts />
            </ProtectedRoute>
          }
        />

        <Route
          path="/inventory/drafts/new"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <DraftForm />
            </ProtectedRoute>
          }
        />

        <Route
          path="/inventory/drafts/edit/:id"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <DraftForm />
            </ProtectedRoute>
          }
        />

        <Route
          path="/add-product"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <AddProduct />
            </ProtectedRoute>
          }
        />

        <Route
          path="/edit/:sku"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <Editor />
            </ProtectedRoute>
          }
        />

        <Route
          path="/platforms/shopify"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <ShopifyInventory />
            </ProtectedRoute>
          }
        />

        <Route
          path="/platforms/ebay"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <EbayInventory />
            </ProtectedRoute>
          }
        />

        <Route
          path="/platforms/walmart"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <WalmartInventory />
            </ProtectedRoute>
          }
        />

        <Route
          path="/orders"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <Orders />
            </ProtectedRoute>
          }
        />

        <Route
          path="/purchases"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <Purchases />
            </ProtectedRoute>
          }
        />
        <Route
          path="/purchase-batches"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <PurchaseBatches />
            </ProtectedRoute>
          }
        />

        <Route
          path="/platforms"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <Platforms />
            </ProtectedRoute>
          }
        />

        <Route
          path="/products"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <Products />
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <Reports />
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <Accounting />
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/mercari"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <AccountingMercari />
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/poshmark"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <AccountingPoshmark />
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/shopify"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <AccountingShopify />
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/walmart"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <AccountingWalmart />
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/all"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <AccountingAll />
            </ProtectedRoute>
          }
        />

        <Route
          path="/tools/sku"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <SKUGenerator />
            </ProtectedRoute>
          }
        />

        <Route
          path="/tools/create-order"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <CreateOrder />
            </ProtectedRoute>
          }
        />

        <Route
          path="/tools/barcode"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <BarcodeGenerator />
            </ProtectedRoute>
          }
        />

        <Route
          path="/tools/bulk-uploader"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <BulkUploader />
            </ProtectedRoute>
          }
        />

        <Route
          path="/bin-assignment"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <BinAssignment />
            </ProtectedRoute>
          }
        />

        <Route
          path="/picklist"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <PickList />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <Settings role={role} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/admin"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              {role === 'OWNER' ? (
                <Admin />
              ) : (
                <div>Forbidden</div>
              )}
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/connected-accounts"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <ConnectedAccounts />
            </ProtectedRoute>
          }
        />

        <Route
          path="/users/add"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              {role === 'OWNER' ? (
                <AddUser />
              ) : (
                <div>Forbidden</div>
              )}
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/calendar"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <CalendarSettings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/restock"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <Restock />
            </ProtectedRoute>
          }
        />

        <Route
          path="/quick-buy"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <QuickBuy />
            </ProtectedRoute>
          }
        />

        <Route
          path="/goals"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <Goals />
            </ProtectedRoute>
          }
        />

        <Route
          path="/budget"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <Budget />
            </ProtectedRoute>
          }
        />

        <Route
          path="/card-intake-monitor"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
            >
              <CardIntakeMonitor />
            </ProtectedRoute>
          }
        />
      </Routes>
    </div>
  );
}

// Global Error Boundary
class ErrorBoundary extends React.Component {
  state = {
    error: null,
  };

  static getDerivedStateFromError(error) {
    return {
      error: error.message,
    };
  }

  render() {
    if (this.state.error) {
      return (
        <Container className="mt-5">
          <Alert variant="danger">
            <h4>Application Error</h4>

            <p>{this.state.error}</p>

            <p>
              Please try refreshing the page or contact support if
              the issue persists.
            </p>

            <Button
              variant="primary"
              onClick={() => window.location.reload()}
            >
              Refresh
            </Button>
          </Alert>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default function AppWrapper() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </BrowserRouter>
  );
}